import type { CSSProperties } from 'react'
import { cn } from '@/utils/cn'
import { useSourceBadges } from '@/contexts/SourceBadgesContext'
import { badgeTextColors } from '@/utils/badgeColor'

/**
 * Says where a sense came from, using the badge an admin configured for that
 * source. A source with no badge shows nothing — absence is the default, not a
 * signal — so these appear only on the detail surfaces, never in running text.
 *
 * A badge is a dot **plus a visible label**: colour alone would be the only
 * channel carrying the meaning, which fails for colour-blind and greyscale
 * readers. The dot is decorative; the label is the message.
 */
export function SourceBadge({ source, className }: { source: string; className?: string }) {
  const badge = useSourceBadges()[source]
  if (!badge) return null

  return <SourceBadgeChip label={badge.label} color={badge.color} className={className} />
}

/**
 * The chip itself, drawn from values rather than from the configuration. The
 * admin screen renders it straight from the form so the preview tracks the
 * colour wheel before anything is saved.
 */
export function SourceBadgeChip({
  label,
  color,
  className,
}: {
  label: string
  color: string
  className?: string
}) {
  const { light, dark } = badgeTextColors(color)

  return (
    <span
      className={cn(
        'source-badge inline-flex items-center gap-1 align-middle shrink-0',
        'text-[11px] font-light uppercase tracking-wider select-none',
        className,
      )}
      style={{ '--badge-light': light, '--badge-dark': dark } as CSSProperties}
    >
      <span aria-hidden="true" className="size-1.5 rounded-full bg-current" />
      <span aria-hidden="true">{label}</span>
      <span className="sr-only">source: {label}</span>
    </span>
  )
}

/**
 * One badge per distinct source, for headings that stand in for several senses.
 * Order follows the sources given, so the payload decides it.
 */
export function SourceBadgeList({ sources, className }: { sources: string[]; className?: string }) {
  return [...new Set(sources)].map((source) => (
    <SourceBadge key={source} source={source} className={className} />
  ))
}
